import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/design-system/button';
import { LocationPreview } from '@/design-system/location-preview';
import {
  type PlacePick,
  PlacesAutocomplete,
} from '@/design-system/places-autocomplete';
import { SearchSelect } from '@/design-system/search-select';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { onSessionClear } from '@/utils/sessionReset';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import type {
  CityRef,
  ClientRef,
  DestinationPlace,
  GeocodeResult,
} from '../types';
import { DestinationMap, type MapPoint } from './DestinationMap';
import { FormField, TextInput } from './form';
import { CitySelect } from './pickers';
import { metaCls } from './ui';

/**
 * Outbound request wizard, step "Penerima & tujuan" (REGULAR orders). The
 * client's destination config decides the input:
 *  - consumer delivery (`destinationToClient`): typed recipient + address,
 *    coordinates from the cache-first geocode (address blur / "Cari
 *    koordinat");
 *  - B2B: a "Tujuan" picker over the client's known places, with
 *    "Alamat lain…" for a manual address (same geocode lookup).
 * TRANSFER keeps plain typed fields. The request payload is unchanged.
 */

export interface DestinationDraft {
  recipientName: string;
  recipientPhone: string;
  address: string;
  latitude: string;
  longitude: string;
  destNotes: string;
  /** B2B: "Alamat lain…" chosen instead of a known place. */
  manual: boolean;
  /** What the last geocode matched (shown under the coordinates). */
  matched: Pick<GeocodeResult, 'address' | 'source'> | null;
  /** Destination city — always a core master city: resolved by the server
   *  from the address / point, or picked by ops (`cityTouched`). */
  city: CityRef | null;
  cityTouched: boolean;
  /** The server found nothing for the address / point: ops picks by hand. */
  cityWarning: string | null;
}

export const emptyDestination = (): DestinationDraft => ({
  recipientName: '',
  recipientPhone: '',
  address: '',
  latitude: '',
  longitude: '',
  destNotes: '',
  manual: false,
  matched: null,
  city: null,
  cityTouched: false,
  cityWarning: null,
});

export type DestinationField =
  | 'recipientName'
  | 'recipientPhone'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'destNotes'
  | 'city';

interface Props {
  client: ClientRef | null;
  transfer: boolean;
  /** Typed recipient + address with the geocode and map, without the
   *  client's known places (Shipments "Ubah"). */
  addressOnly?: boolean;
  value: DestinationDraft;
  onChange: (patch: Partial<DestinationDraft>) => void;
  fieldError: (key: DestinationField) => string | null | undefined;
}

/* ------------------------------ client config ---------------------------- */

const configCache = new Map<number, boolean>();

type ConfigState =
  { status: 'loading' } | { status: 'ready'; toClient: boolean };

/** `destinationToClient` per client, fetched once per session. */
const useDestinationConfig = (clientId: number | null): ConfigState => {
  const cached = clientId !== null ? configCache.get(clientId) : undefined;
  const [state, setState] = useState<ConfigState>(
    cached !== undefined
      ? { status: 'ready', toClient: cached }
      : { status: 'loading' }
  );
  useEffect(() => {
    if (clientId === null) return undefined;
    const hit = configCache.get(clientId);
    if (hit !== undefined) {
      setState({ status: 'ready', toClient: hit });
      return undefined;
    }
    setState({ status: 'loading' });
    const controller = new AbortController();
    warehouseApi
      .getDestinationConfig(clientId, { signal: controller.signal })
      .then((res) => {
        const toClient = !!res?.destinationToClient;
        configCache.set(clientId, toClient);
        setState({ status: 'ready', toClient });
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        // Unknown config: typed address + geocode still works for any client.
        setState({ status: 'ready', toClient: true });
      });
    return () => controller.abort();
  }, [clientId]);
  return state;
};

/** Forget cached client configs (sign-out, tests). */
export const resetDestinationConfigCache = () => configCache.clear();
onSessionClear(resetDestinationConfigCache);

/* -------------------------------- geocode -------------------------------- */

const GEOCODE_NOT_FOUND =
  'Alamat tidak ditemukan — periksa atau isi koordinat manual.';
const MIN_ADDRESS = 5;

const fmtCoord = (n: number) => String(Math.round(n * 1e7) / 1e7);

/** A typed coordinate, or null while it is empty / not a number / out of range. */
const parseCoord = (raw: string, limit: number): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

const NUM = '(-?\\d+(?:[.,]\\d+)?)';
/** "-6.2615, 106.8139" / "-6.2615 106.8139" / "-6.2615,106.8139" — what Google
 *  Maps copies — typed or pasted into either field. */
const PAIR_SPACED = new RegExp(`^${NUM}\\s*[,;]?\\s+${NUM}$`);
const PAIR_TIGHT = /^(-?\d+\.\d+),(-?\d+\.\d+)$/;

/**
 * What a coordinate field holds after a keystroke / paste: a lat-long pair
 * fills both fields; a decimal comma ("-6,2615") becomes a point. Anything
 * else stays as typed (the field shows why it is not a coordinate).
 */
export const coordInput = (
  raw: string
): { latitude: string; longitude: string } | { value: string } => {
  const t = raw.trim();
  const m = t.match(PAIR_SPACED) ?? t.match(PAIR_TIGHT);
  if (m) {
    const lat = m[1].replace(',', '.');
    const lng = m[2].replace(',', '.');
    if (parseCoord(lat, 90) !== null && parseCoord(lng, 180) !== null) {
      return { latitude: lat, longitude: lng };
    }
  }
  return {
    value:
      !raw.includes('.') && raw.split(',').length === 2
        ? raw.replace(',', '.')
        : raw,
  };
};

/** Local message for a coordinate that is not one (typing "-" is fine). */
const coordError = (raw: string, limit: 90 | 180): string | null => {
  const t = raw.trim();
  if (t === '' || t === '-' || parseCoord(t, limit) !== null) return null;
  return limit === 90
    ? 'Latitude harus angka -90 s.d. 90, mis. -6.2615.'
    : 'Longitude harus angka -180 s.d. 180, mis. 106.8139.';
};

/** Read-only pin for a client's saved place (its coordinates are not edited here). */
const DestinationPreview: React.FC<{ value: DestinationDraft }> = ({
  value,
}) => (
  <div data-testid="oo-location-preview">
    <LocationPreview
      latitude={parseCoord(value.latitude, 90)}
      longitude={parseCoord(value.longitude, 180)}
      height={180}
      emptyHint="Belum ada koordinat."
    />
  </div>
);

/* ------------------------------ places picker ---------------------------- */

const OTHER = '__other__';

interface PlaceOption {
  id: string;
  name: string;
  place: DestinationPlace;
}

const placeId = (p: DestinationPlace) =>
  `${p.latitude},${p.longitude}|${p.name}`;

/**
 * Alamat tujuan with Google Places suggestions (kit PlacesAutocomplete, as in
 * the Address menu): picking one fills the coordinates. The live field mounts
 * on focus — mounted with a prefilled value (back from a later step) it would
 * open its suggestion list on its own.
 */
const AddressSuggestInput: React.FC<{
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
  onPick: (place: PlacePick) => void;
  onBlur?: () => void;
}> = ({ value, invalid, onChange, onPick, onBlur }) => {
  const [live, setLive] = useState(false);
  const liveRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (live) liveRef.current?.focus();
  }, [live]);
  if (!live) {
    return (
      <TextInput
        id="oo-address"
        size="md"
        icon="ri-map-pin-line"
        value={value}
        onChange={onChange}
        onFocus={() => setLive(true)}
        onBlur={onBlur}
        invalid={invalid}
        autoComplete="off"
        rootClassName="flex-1"
      />
    );
  }
  return (
    <div
      className="min-w-0 flex-1"
      data-testid="oo-address-suggest"
      onBlur={(e) => {
        // Focus moving into the suggestion list is not leaving the field.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          onBlur?.();
      }}
    >
      <PlacesAutocomplete
        ref={liveRef}
        inputId="oo-address"
        value={value}
        onChange={onChange}
        onPick={onPick}
        size="md"
        showHint={false}
      />
    </div>
  );
};

/* -------------------------------- the step ------------------------------- */

const OutboundDestinationStep: React.FC<Props> = ({
  client,
  transfer,
  addressOnly = false,
  value,
  onChange,
  fieldError: fe,
}) => {
  const clientId = client?.id ?? null;
  const config = useDestinationConfig(
    transfer || addressOnly ? null : clientId
  );
  const [geo, setGeo] = useState<{
    loading: boolean;
    error: string | null;
    /** Address the last lookup was for (blur only re-runs on a change). */
    for: string;
  }>(() => ({
    loading: false,
    error: null,
    // A draft that already has coordinates (an edit, or back from a later
    // step) is resolved: a blur must not geocode over them.
    for:
      value.latitude.trim() && value.longitude.trim()
        ? value.address.trim()
        : '',
  }));
  const geoAbort = useRef<AbortController | null>(null);
  useEffect(() => () => geoAbort.current?.abort(), []);
  /** The map's pin = the typed / resolved coordinates; moving it types them
   *  back. While a field holds something that is not a coordinate (mid-typing,
   *  a typo) the pin stays where it last was instead of vanishing. */
  const lastPoint = useRef<MapPoint | null>(null);
  const currentPoint: MapPoint | null = (() => {
    const lat = parseCoord(value.latitude, 90);
    const lng = parseCoord(value.longitude, 180);
    if (lat !== null && lng !== null) {
      lastPoint.current = { lat, lng };
    } else if (!value.latitude.trim() && !value.longitude.trim()) {
      lastPoint.current = null;
    }
    return lastPoint.current;
  })();
  /** A keystroke / paste in Latitude or Longitude (see `coordInput`). */
  const typeCoord = (field: 'latitude' | 'longitude', raw: string) => {
    const next = coordInput(raw);
    onChange(
      'value' in next
        ? { [field]: next.value, matched: null }
        : { ...next, matched: null }
    );
  };
  const latError = fe('latitude') ?? coordError(value.latitude, 90);
  const lngError = fe('longitude') ?? coordError(value.longitude, 180);
  const destinationMap = (
    <DestinationMap
      point={currentPoint}
      onChange={(p) => {
        // A hand-placed pin wins over any geocode still running.
        geoAbort.current?.abort();
        setGeo({ loading: false, error: null, for: value.address.trim() });
        onChange({
          latitude: fmtCoord(p.lat),
          longitude: fmtCoord(p.lng),
          matched: null,
        });
      }}
    />
  );
  /** Latest draft, for answers that arrive after the user moved on. */
  const latest = useRef(value);
  latest.current = value;

  const lookup = useCallback(
    async (force: boolean) => {
      const address = value.address.trim();
      if (address.length < MIN_ADDRESS) {
        if (force)
          setGeo((g) => ({
            ...g,
            error: `Alamat minimal ${MIN_ADDRESS} karakter.`,
          }));
        return;
      }
      if (!force && address === geo.for) return;
      geoAbort.current?.abort();
      const controller = new AbortController();
      geoAbort.current = controller;
      setGeo({ loading: true, error: null, for: address });
      const typed = { lat: value.latitude, lng: value.longitude };
      try {
        const res = await warehouseApi.geocodeAddress(address, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        // Coordinates typed while the lookup ran win over the answer.
        const now = latest.current;
        if (now.latitude !== typed.lat || now.longitude !== typed.lng) {
          setGeo({ loading: false, error: null, for: address });
          return;
        }
        onChange({
          latitude: fmtCoord(res.latitude),
          longitude: fmtCoord(res.longitude),
          matched: { address: res.address, source: res.source },
        });
        setGeo({ loading: false, error: null, for: address });
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err)) return;
        const e = parseWarehouseError(err);
        onChange({ matched: null });
        setGeo({
          loading: false,
          error:
            e.status === 404 || e.code === 'GEOCODE_NOT_FOUND'
              ? GEOCODE_NOT_FOUND
              : (e.fieldErrors[0]?.message ?? e.message),
          for: address,
        });
      }
    },
    [value.address, value.latitude, value.longitude, geo.for, onChange]
  );

  const consumer = addressOnly
    ? true
    : transfer || config.status !== 'ready'
      ? null
      : config.toClient;

  /* ------------------------------- city ------------------------------- */

  // The destination city is the server's call (core's city master matched
  // from the address text — no geocoding). Ask again whenever the address
  // settles — unless ops picked one by hand.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const cityAbort = useRef<AbortController | null>(null);
  useEffect(() => () => cityAbort.current?.abort(), []);
  const cityAddress = value.address.trim();
  useEffect(() => {
    if (transfer || addressOnly || value.cityTouched) return;
    if (cityAddress.length < MIN_ADDRESS) return;
    cityAbort.current?.abort();
    const controller = new AbortController();
    cityAbort.current = controller;
    const timer = setTimeout(() => {
      warehouseApi
        .resolveOutboundCity(
          { address: cityAddress },
          { signal: controller.signal }
        )
        .then((res) => {
          if (controller.signal.aborted) return;
          onChangeRef.current({ city: res.city, cityWarning: res.warning });
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) return;
          onChangeRef.current({
            cityWarning: 'Kota tidak bisa dicek — pilih manual.',
          });
        });
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [transfer, addressOnly, value.cityTouched, cityAddress]);

  // No city on a transfer (the destination is a hub) or a shipment edit
  // (addressOnly — Shipments has no WMS city).
  const cityField =
    transfer || addressOnly ? null : (
    <FormField
      label="Kota tujuan"
      htmlFor="oo-city"
      optional
      error={fe('city')}
    >
      <CitySelect
        value={value.city}
        onChange={(c) =>
          onChange({ city: c, cityTouched: true, cityWarning: null })
        }
        placeholder="Pilih kota (master core-service)"
        allowClear
        invalid={!!fe('city')}
        aria-label="Kota tujuan"
      />
      {!value.city && value.cityWarning && (
        <p
          className="mt-1 text-xs text-warning-base"
          data-testid="oo-city-warning"
        >
          <i className="ri-error-warning-line mr-1" />
          {value.cityWarning}
        </p>
      )}
      {value.city && !value.cityTouched && (
        <p className={metaCls} data-testid="oo-city-auto">
          Terdeteksi otomatis dari teks alamat
        </p>
      )}
    </FormField>
  );

  /* ------------------------------ fields ------------------------------- */

  const nameField = (placeholder?: string) => (
    <FormField
      label="Nama penerima"
      htmlFor="oo-name"
      required
      error={fe('recipientName')}
    >
      <TextInput
        id="oo-name"
        size="md"
        icon="ri-user-line"
        value={value.recipientName}
        onChange={(v) => onChange({ recipientName: v })}
        placeholder={placeholder}
        invalid={!!fe('recipientName')}
      />
    </FormField>
  );

  const phoneField = (
    <FormField
      label="Telepon"
      htmlFor="oo-phone"
      required
      error={fe('recipientPhone')}
    >
      <TextInput
        id="oo-phone"
        size="md"
        icon="ri-phone-line"
        value={value.recipientPhone}
        onChange={(v) => onChange({ recipientPhone: v })}
        placeholder="08… / 021… / +62…"
        inputMode="tel"
        invalid={!!fe('recipientPhone')}
      />
    </FormField>
  );

  const notesField = (
    <FormField
      label="Catatan tujuan"
      htmlFor="oo-dest-notes"
      optional
      error={fe('destNotes')}
    >
      <TextInput
        id="oo-dest-notes"
        size="md"
        value={value.destNotes}
        onChange={(v) => onChange({ destNotes: v })}
      />
    </FormField>
  );

  /** Alamat + coordinates; `geocode` adds the lookup (not for TRANSFER). */
  const addressFields = (geocode: boolean) => {
    const addressError = fe('address') ?? (geocode ? geo.error : null);
    return (
      <>
        <FormField
          label="Alamat tujuan"
          htmlFor="oo-address"
          required
          error={addressError}
        >
          <div className="flex items-center gap-2">
            <AddressSuggestInput
              value={value.address}
              invalid={!!addressError}
              onChange={(v) => {
                // The old match no longer describes the typed text.
                onChange({ address: v, matched: null });
                if (geo.error) setGeo((g) => ({ ...g, error: null }));
              }}
              onPick={(place) => {
                geoAbort.current?.abort();
                // Picked = resolved: blur must not geocode it again.
                setGeo({
                  loading: false,
                  error: null,
                  for: place.address.trim(),
                });
                onChange({
                  address: place.address,
                  latitude: fmtCoord(place.latitude),
                  longitude: fmtCoord(place.longitude),
                  matched: { address: place.address, source: 'GOOGLE' },
                });
              }}
              onBlur={geocode ? () => void lookup(false) : undefined}
            />
            {geocode && (
              <Button
                tone="neutral"
                style="stroke"
                loading={geo.loading}
                data-testid="oo-geocode"
                onClick={() => void lookup(true)}
                leftIcon={<i className="ri-crosshair-2-line text-[14px]" />}
              >
                Cari koordinat
              </Button>
            )}
          </div>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Latitude"
            htmlFor="oo-lat"
            required
            error={latError}
          >
            <TextInput
              id="oo-lat"
              size="md"
              inputMode="decimal"
              value={value.latitude}
              onChange={(v) => typeCoord('latitude', v)}
              placeholder="-6.9175"
              invalid={!!latError}
              className="font-mono"
            />
          </FormField>
          <FormField
            label="Longitude"
            htmlFor="oo-lng"
            required
            error={lngError}
          >
            <TextInput
              id="oo-lng"
              size="md"
              inputMode="decimal"
              value={value.longitude}
              onChange={(v) => typeCoord('longitude', v)}
              placeholder="107.6091"
              invalid={!!lngError}
              className="font-mono"
            />
          </FormField>
        </div>
        {geocode && value.matched && (
          <p className={metaCls} data-testid="oo-geocode-match">
            {value.matched.source === 'CACHE'
              ? 'Koordinat dari geocode cache'
              : 'Koordinat dari Google'}
            : {value.matched.address}
          </p>
        )}
        {destinationMap}
      </>
    );
  };

  if (transfer) {
    return (
      <div className="flex flex-col gap-3" data-testid="oo-destination">
        <div className="grid grid-cols-2 gap-3">
          {nameField()}
          {phoneField}
        </div>
        {addressFields(false)}
        {cityField}
        {notesField}
      </div>
    );
  }

  if (consumer === null) {
    return (
      <div
        className="flex h-24 items-center justify-center text-sm text-text-sub-600"
        role="status"
        data-testid="oo-destination-loading"
      >
        Memuat tujuan…
      </div>
    );
  }

  if (consumer) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="oo-destination"
        data-mode="consumer"
      >
        <div className="grid grid-cols-2 gap-3">
          {nameField()}
          {phoneField}
        </div>
        {addressFields(true)}
        {cityField}
        {notesField}
      </div>
    );
  }

  const destError = fe('address') ?? fe('latitude') ?? fe('longitude') ?? null;
  const placeError = value.manual
    ? null
    : destError && !value.address
      ? 'Tujuan wajib dipilih.'
      : destError;

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="oo-destination"
      data-mode={value.manual ? 'manual' : 'place'}
    >
      <PlaceField
        clientId={clientId}
        value={value}
        error={placeError}
        onPick={(place) => {
          setGeo({ loading: false, error: null, for: '' });
          onChange(
            place
              ? {
                  manual: false,
                  recipientName: place.name,
                  address: place.address,
                  latitude: fmtCoord(place.latitude),
                  longitude: fmtCoord(place.longitude),
                  matched: null,
                }
              : {
                  manual: true,
                  recipientName: '',
                  address: '',
                  latitude: '',
                  longitude: '',
                  matched: null,
                }
          );
        }}
      />
      <div className="grid grid-cols-2 gap-3">
        {nameField()}
        {phoneField}
      </div>
      {value.manual && addressFields(true)}
      {cityField}
        {notesField}
    </div>
  );
};

/* ------------------------------- Tujuan field ---------------------------- */

const PlaceField: React.FC<{
  clientId: number | null;
  value: DestinationDraft;
  error: string | null;
  /** null = "Alamat lain…". */
  onPick: (place: DestinationPlace | null) => void;
}> = ({ clientId, value, error, onPick }) => {
  const fetchPlaces = useCallback(
    (search?: string): Promise<PlaceOption[]> =>
      clientId === null
        ? Promise.resolve([])
        : warehouseApi
            .listDestinationPlaces({ clientId, search: search ?? null })
            .then((places) =>
              places.map((p) => ({ id: placeId(p), name: p.name, place: p }))
            ),
    [clientId]
  );
  const {
    options,
    loading,
    error: loadError,
    setSearch,
  } = useSearchableOptions(clientId !== null, fetchPlaces);
  const selectOptions = useMemo(
    () => [
      ...options.map((o) => ({
        value: o.id,
        label: o.place.address ? `${o.name} · ${o.place.address}` : o.name,
      })),
      { value: OTHER, label: 'Alamat lain…' },
    ],
    [options]
  );
  const picked =
    !value.manual && value.address
      ? placeId({
          name: value.recipientName,
          address: value.address,
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
        })
      : '';
  const current = value.manual ? OTHER : picked;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const input = ref.current?.querySelector('input');
    if (input) input.setAttribute('aria-label', 'Tujuan');
  });
  return (
    <FormField label="Tujuan" required error={error} testId="oo-place">
      <div ref={ref}>
        <SearchSelect
          options={selectOptions}
          value={current}
          selectedLabelFallback={
            value.manual
              ? 'Alamat lain…'
              : value.address
                ? `${value.recipientName} · ${value.address}`
                : undefined
          }
          onChange={(v) => {
            if (!v) return;
            if (v === OTHER) {
              onPick(null);
              return;
            }
            const o = options.find((x) => x.id === v);
            if (o) onPick(o.place);
          }}
          onSearchChange={setSearch}
          disableLocalFilter
          loading={loading}
          placeholder="Cari tujuan"
          error={!!error}
          emptyMessage={loadError ?? 'Tujuan tidak ditemukan'}
        />
      </div>
      {!value.manual && value.address && (
        <>
          <p className={metaCls} data-testid="oo-place-summary">
            {value.address} · {value.latitude}, {value.longitude}
          </p>
          <DestinationPreview value={value} />
        </>
      )}
    </FormField>
  );
};

export default OutboundDestinationStep;
